import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Button } from '../../components/admin/shared/Button';
import { Input } from '../../components/admin/shared/Input';
import { Textarea } from '../../components/admin/shared/Textarea';
import { Modal } from '../../components/admin/shared/Modal';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import { Plus, Edit, Trash2, Star, Upload } from 'lucide-react';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    company: '',
    content: '',
    rating: 5,
    featured: false,
    image: ''
  });
  const [uploading, setUploading] = useState(false);
  const { toasts, addToast } = useToast();

  const fetchTestimonials = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('featured', { ascending: false })
        .order('order', { ascending: true });

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      addToast('Error loading testimonials', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('portfolio-images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('portfolio-images')
        .getPublicUrl(fileName);
      
      setFormData({ ...formData, image: data.publicUrl });
      addToast('Image uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      addToast('Error uploading image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = () => {
    setEditingTestimonial(null);
    setFormData({
      name: '',
      role: '',
      company: '',
      content: '',
      rating: 5,
      featured: false,
      image: ''
    });
    setShowModal(true);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name || '',
      role: testimonial.role || '',
      company: testimonial.company || '',
      content: testimonial.content || '',
      rating: testimonial.rating || 5,
      featured: testimonial.featured || false,
      image: testimonial.image || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        name: formData.name,
        role: formData.role,
        company: formData.company,
        content: formData.content,
        rating: formData.rating,
        featured: formData.featured,
        image: formData.image
      };

      if (editingTestimonial) {
        const { error } = await supabase
          .from('testimonials')
          .update(data)
          .eq('id', editingTestimonial.id);

        if (error) throw error;
        addToast('Testimonial updated successfully', 'success');
      } else {
        const { error } = await supabase
          .from('testimonials')
          .insert(data);

        if (error) throw error;
        addToast('Testimonial added successfully', 'success');
      }
      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      addToast('Error saving testimonial', 'error');
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Delete this testimonial?')) return;

    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', testimonialId);

      if (error) throw error;
      addToast('Testimonial deleted successfully', 'success');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      addToast('Error deleting testimonial', 'error');
    }
  };

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Testimonials</h1>
        <Button
          variant="primary"
          onClick={handleAdd}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Add Testimonial
        </Button>
      </div>

      {loading ? (
        <div className="text-white">Loading...</div>
      ) : testimonials.length === 0 ? (
        <div className="bg-slate-800 rounded-lg p-12 text-center border border-slate-700">
          <p className="text-slate-400 mb-4">No testimonials yet.</p>
          <Button variant="primary" onClick={handleAdd}>
            Add Testimonial
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-slate-800 rounded-lg p-6 border border-slate-700"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <p className="text-slate-300 mb-4 italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-4">
                    {testimonial.image && (
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-slate-400">
                        {testimonial.role}
                        {testimonial.company && ` at ${testimonial.company}`}
                      </p>
                    </div>
                  </div>
                  {testimonial.rating && (
                    <div className="flex gap-1 mt-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(testimonial)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(testimonial.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
      >
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          label="Role"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          placeholder="e.g., CEO, Manager"
        />
        <Input
          label="Company"
          value={formData.company}
          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
        />
        <Textarea
          label="Testimonial"
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="What they said..."
        />
        <div className="mb-4">
          <label className="block text-sm text-slate-400 mb-2">Rating</label>
          <select
            value={formData.rating}
            onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-violet-500"
          >
            {[1, 2, 3, 4, 5].map(r => (
              <option key={r} value={r}>{r} {r === 1 ? 'star' : 'stars'}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-400">Featured</span>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-sm text-slate-400 mb-2">Photo</label>
          {formData.image && (
            <img src={formData.image} alt="Preview" className="w-20 h-20 rounded-full object-cover mb-2" />
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <Upload size={20} className="text-slate-400" />
            <span className="text-slate-400">Choose File</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImageUpload(file);
              }}
              disabled={uploading}
            />
          </label>
          {uploading && <p className="text-slate-400 text-sm mt-2">Uploading...</p>}
        </div>
        <div className="flex gap-4 mt-6">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editingTestimonial ? 'Update' : 'Add'} Testimonial
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Testimonials;

